import { type BoxStaffApiGateway } from "@modules/boxStaff/gateway/boxStaffApiGateway"
import {
  type BoxStaffDetailedDto,
  type BoxStaffInvitationDetailsDto,
  type BoxStaffItemDto,
  BoxStaffRole,
  BoxStaffStatus,
} from "@modules/boxStaff/gateway/boxStaffDto"
import { USER_TEST_DATA } from "@modules/profile/publicProfile/test/publicProfileApi.fake"
import { type Page, toPage } from "@modules/utils/paginated"

const _BOX_STAFF_OWNER = {
  id: "owner_id",
  userId: "owner_user_id",
  boxId: "default_box_id",
  role: BoxStaffRole.OWNER,
  status: BoxStaffStatus.ACCEPTED,
  firstname: "owner_firstname",
  lastname: "owner_lastname",
  email: "owner@example.com",
  avatar: null,
  creationDate: "2021-01-01T00:00:00Z",
  username: "ownerUsername",
  internalNote: "The internal note of owner",
  archived: false,
}

const _BOX_STAFF_COOWNER = {
  id: "co_owner_id",
  userId: "co_owner_user_id",
  boxId: "default_box_id",
  role: BoxStaffRole.CO_OWNER,
  status: BoxStaffStatus.PENDING,
  firstname: "co_owner_firstname",
  lastname: "co_owner_lastname",
  email: "co-owner@exemple.com",
  avatar: null,
  creationDate: "2022-08-07T00:00:00Z",
  username: "coOwnerUsername",
  internalNote: "The internal note of co owner",
  archived: false,
}

const _BOX_STAFF_HEADCOACH = {
  id: "head_coach_id",
  userId: "head_coach_user_id",
  boxId: "default_box_id",
  role: BoxStaffRole.HEAD_COACH,
  status: BoxStaffStatus.ACCEPTED,
  firstname: "head_coach_firstname",
  lastname: "head_coach_lastname",
  email: "head_coach@exemple.com",
  avatar: null,
  creationDate: "2023-10-07T00:00:00Z",
  username: "headCoachUsername",
  internalNote: "The internal note of head coach",
  archived: false,
}

const _BOX_STAFF_COACH = {
  id: "coach_id",
  userId: "coach_user_id",
  boxId: "default_box_id",
  role: BoxStaffRole.COACH,
  status: BoxStaffStatus.ACCEPTED,
  firstname: "coach_firstname",
  lastname: "coach_lastname",
  email: "coach@exemple.com",
  avatar: null,
  creationDate: "2024-08-07T00:00:00Z",
  username: "coachUsername",
  internalNote: "The internal note of coach",
  archived: false,
}

const _BOX_STAFF_INVITATION_DETAILS: BoxStaffInvitationDetailsDto = {
  boxId: "default_box_id",
  inviter: USER_TEST_DATA.OWNER,
  role: BoxStaffRole.COACH,
  status: BoxStaffStatus.PENDING,
}

const _BOX_STAFF_ITEMS_DATA: BoxStaffItemDto[] = [
  _BOX_STAFF_OWNER,
  _BOX_STAFF_COOWNER,
  _BOX_STAFF_HEADCOACH,
  _BOX_STAFF_COACH,
]

const _BOX_STAFF_DETAILS_OWNER_DATA: BoxStaffDetailedDto = {
  ..._BOX_STAFF_OWNER,
  avatar: undefined,
}

const _BOX_STAFF_DETAILS_COOWNER_DATA: BoxStaffDetailedDto = {
  ..._BOX_STAFF_COOWNER,
  avatar: undefined,
}

const _BOX_STAFF_DETAILS_DATA: BoxStaffDetailedDto[] = [_BOX_STAFF_DETAILS_OWNER_DATA, _BOX_STAFF_DETAILS_COOWNER_DATA]

export const BOX_STAFF_TEST_DATA = {
  OWNER: _BOX_STAFF_OWNER,
  CO_OWNER: _BOX_STAFF_COOWNER,
  HEAD_COACH: _BOX_STAFF_HEADCOACH,
  COACH: _BOX_STAFF_COACH,
  ITEMS: _BOX_STAFF_ITEMS_DATA,
  DETAILS: _BOX_STAFF_DETAILS_DATA,
}

export class FakeBoxStaffApi implements BoxStaffApiGateway {
  private boxStaffsItemArray: BoxStaffItemDto[] = _BOX_STAFF_ITEMS_DATA
  private boxStaffDetailedArray: BoxStaffDetailedDto[] = _BOX_STAFF_DETAILS_DATA

  givenBoxStaffDetailedByRole(boxStaffRole: BoxStaffRole): BoxStaffDetailedDto {
    const staff = this.boxStaffDetailedArray.find((staff) => staff.role === boxStaffRole)
    if (!staff) {
      throw new Error("Staff not found")
    }
    return staff
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getBoxStaffs(page = 0, size = 10): Promise<Page<BoxStaffItemDto>> {
    const result = {
      content: this.boxStaffsItemArray,
      contentSize: 2,
      offset: 0,
      page,
      pageSize: size,
      totalElements: 2,
      totalPages: 1,
      last: true,
    }

    const pageResult = toPage(result)

    return pageResult
  }

  async getBoxStaff(staffId: string): Promise<BoxStaffDetailedDto> {
    const staff = this.boxStaffDetailedArray.find((staff) => staff.id === staffId)
    if (!staff) {
      throw new Error("Staff not found")
    }
    return Promise.resolve({
      ...staff,
    })
  }

  async addBoxStaff(): Promise<BoxStaffItemDto> {
    const newStaff: BoxStaffItemDto = {
      id: "new_id",
      userId: "new_user_id",
      boxId: "default_box_id",
      role: BoxStaffRole.COACH,
      status: BoxStaffStatus.PENDING,
      firstname: "new_firstname",
      lastname: "new_lastname",
      email: "new@example.com",
      avatar: null,
      creationDate: new Date().toISOString(),
      username: "newUsername",
      internalNote: "The internal note of new staff",
      archived: false,
    }
    this.boxStaffsItemArray.push(newStaff)
    return Promise.resolve(newStaff)
  }

  async responseBoxStaffInvitation(): Promise<void> {
    return Promise.resolve()
  }

  async updateBoxStaff(): Promise<void> {
    return Promise.resolve()
  }

  async deleteBoxStaff(): Promise<void> {
    return Promise.resolve()
  }

  async getBoxStaffInvitationDetails(_boxId: string): Promise<BoxStaffInvitationDetailsDto> {
    return Promise.resolve(_BOX_STAFF_INVITATION_DETAILS)
  }
}
